import type { SimulatedMailResult } from '@myrewards/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { Dialog, Form, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { useNavigate } from 'react-router';
import { isApiError } from '../api';
import { simulateAssistantMail } from '../api/ai';
import { sentMailKeys } from '../api/notifications';
import { PersonPicker, type PickedPerson } from '../settings/PersonPicker';
import { Button, Icon, TextField, useToast } from '../ui';
import s from './AssistantMail.module.css';

/** Who the message is from, before and after picking. */
export interface WriteAsPerson {
  id: string;
  name: string;
}

export interface WriteAsDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** The email being answered (Reply as…), or null for a new message to the assistant. */
  replyTo: { id: string; subject: string } | null;
  /** Who it's from to begin with: the email's recipient, when it went to a person. */
  initialPerson: WriteAsPerson | null;
  /** The assistant's address, as the message is written to it. */
  address: string;
}

const firstName = (name: string) => name.split(/\s+/)[0] ?? name;

/** What the dialog says when the assistant didn't answer, keeping it open to try again. */
function outcomeProblem(result: SimulatedMailResult): string | null {
  switch (result.outcome) {
    case 'failed':
      // APP-208: the message is marked failed (APP-198). Its sender is told it couldn't be
      // answered only when nothing may have been done for it; otherwise part may have gone through.
      return result.replies.some((r) => !r.copy)
        ? 'The assistant couldn’t finish it, so it’s marked failed. They were sent a reply saying it couldn’t be answered, and nothing was done. Try again in a moment.'
        : 'The assistant couldn’t finish it, so it’s marked failed, and they weren’t sent a reply. Part of what they asked may already have gone through, so check before trying again.';
    case 'rejected':
      return 'MyRewards couldn’t match their address to exactly one active person, so the message was turned away and nothing was done.';
    case 'ignored':
      return 'The message was ignored as automatic mail, so nothing was done.';
    case 'copy':
      return 'That email went to someone else, so this reads as a copy’s reply-all: it wasn’t answered and nothing was done. Reply as the person it went to.';
    case 'answered':
    case 'working':
      return null;
  }
}

function problemWords(error: unknown): string {
  if (!isApiError(error)) return 'That didn’t go through. Check your connection and try again.';
  switch (error.code) {
    case 'simulated_mail_not_tester':
      return 'Only a named tester can write as someone. Turn on Preview & testing and name yourself as a tester in Settings › System administration › Preview & testing.';
    case 'simulated_mail_unavailable':
      return 'Writing as someone isn’t available in this environment.';
    case 'person_not_active':
      return 'They aren’t an active person in MyRewards, so they can’t write in. Pick someone else.';
    case 'no_email_address':
      return 'The directory has no email address for them, so they can’t write in. Pick someone else.';
    case 'rate_limited':
      return 'That’s a lot of messages in a minute. Wait a moment and try again.';
    default:
      return error.problem?.detail ?? 'That didn’t go through. Try again.';
  }
}

/**
 * Writes to the email assistant as someone (APP-071): a reply to an email in Sent mail, or a new
 * message. Simulated in testing: nothing is sent or received, and it is recorded as written by
 * the System Admin as that person. The assistant reads it like a real email, with that person's
 * permissions, and its answer opens next so the conversation can go on.
 */
export function WriteAsDialog({
  isOpen,
  onOpenChange,
  replyTo,
  initialPerson,
  address,
}: WriteAsDialogProps): ReactElement {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [person, setPerson] = useState<WriteAsPerson | null>(initialPerson);
  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);

  const reset = (): void => {
    setPerson(initialPerson);
    setSubject('');
    setText('');
    setError(null);
    setTried(false);
  };
  const close = (): void => {
    reset();
    onOpenChange(false);
  };

  const submit = async (): Promise<void> => {
    setTried(true);
    if (person === null || text.trim() === '' || (replyTo === null && subject.trim() === ''))
      return;
    setPending(true);
    setError(null);
    try {
      const result = await simulateAssistantMail({
        personId: person.id,
        replyTo: replyTo?.id ?? null,
        subject: replyTo === null ? subject.trim() : '',
        text: text.trim(),
      });
      // Whatever the outcome, any reply it wrote shows in Sent mail behind the dialog.
      await queryClient.invalidateQueries({ queryKey: sentMailKeys.all });
      const problem = outcomeProblem(result);
      if (problem !== null) {
        setError(problem);
        return;
      }
      const answer = result.replies.find((r) => !r.copy);
      close();
      if (result.outcome === 'working' || answer === undefined) {
        toast.show({
          tone: 'info',
          title: `Sent as ${person.name}`,
          body: 'The assistant is still working on it. Its answer will appear in Sent mail.',
        });
        void navigate('/settings/system/sent-mail');
        return;
      }
      toast.show({
        tone: 'done',
        title: `Sent as ${person.name}`,
        body: 'Here’s the assistant’s answer. Reply as them to go on.',
      });
      void navigate(`/settings/system/sent-mail/${answer.id}`);
    } catch (err) {
      setError(problemWords(err));
    } finally {
      setPending(false);
    }
  };

  const pick = (p: PickedPerson): void => {
    setPerson({ id: p.id, name: p.name });
  };

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) close();
      }}
      isDismissable={!pending}
      className={s.overlay}
    >
      <Modal className={s.modal}>
        <Dialog className={s.dialog}>
          <Heading slot="title" className={`disp ${s.title}`}>
            {replyTo === null ? 'Write to the assistant as someone' : 'Reply as someone'}
          </Heading>
          <p className={s.simulated}>
            <Icon name="shield" size={18} />
            <span>
              <strong>Simulated in testing.</strong> No email is sent or received. MyRewards reads
              it as if they had written from their own mailbox, with their permissions, and records
              it as written by you as them.
            </span>
          </p>
          <Form
            className={s.form}
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <dl className={s.envelope}>
              <div>
                <dt>To</dt>
                <dd>{address}</dd>
              </div>
              {replyTo !== null && (
                <div>
                  <dt>Subject</dt>
                  <dd>
                    {/^re:/i.test(replyTo.subject) ? replyTo.subject : `Re: ${replyTo.subject}`}
                  </dd>
                </div>
              )}
            </dl>
            {person === null ? (
              <div className={s.pickerWrap}>
                <PersonPicker
                  label="From"
                  description="The person the assistant will read this as. It acts only with their permissions."
                  exclude={new Set()}
                  onPick={pick}
                  isDisabled={pending}
                />
                {tried && (
                  <p className={s.fieldError} role="alert">
                    Pick who it’s from.
                  </p>
                )}
              </div>
            ) : (
              <div className={s.from}>
                <span className={s.fromLabel}>From</span>
                <span className={s.fromName}>{person.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onPress={() => {
                    setPerson(null);
                  }}
                  isDisabled={pending}
                  aria-label={`Change who it’s from (now ${person.name})`}
                >
                  Change
                </Button>
              </div>
            )}
            {replyTo === null && (
              <TextField
                label="Subject"
                value={subject}
                onChange={setSubject}
                isRequired
                maxLength={200}
                isInvalid={tried && subject.trim() === ''}
                errorMessage="Add a subject, as an email would have."
              />
            )}
            <TextField
              label={replyTo === null ? 'Their message' : 'Their reply'}
              description={
                replyTo === null
                  ? 'Write it as they would, for example “$2,000 each for Ava Petrov and Daniel Cho”.'
                  : 'The email they’re answering is quoted under it, as a mail app does.'
              }
              multiline
              value={text}
              onChange={setText}
              isRequired
              maxLength={10_000}
              isInvalid={tried && text.trim() === ''}
              errorMessage="Write what they say."
            />
            {error !== null && (
              <p className={s.error} role="alert">
                {error}
              </p>
            )}
            <div className={s.actions}>
              <Button variant="ghost" onPress={close} isDisabled={pending}>
                Cancel
              </Button>
              <Button type="submit" variant="navy" isPending={pending}>
                {person === null ? 'Send' : `Send as ${firstName(person.name)}`}
              </Button>
            </div>
          </Form>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
